import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} KissenVariantEntry
 * @property {string} farbe          - The Farbe option value's display text.
 * @property {string} farbeValueId   - The Farbe product_option_value id (matches a swatch input's data-option-value-id).
 * @property {number} variantId      - The first Kissen-only variant id for this color (kissen_groesse set, decke_groesse empty).
 * @property {string} variantUrl     - `${product.url}?variant=${variantId}`.
 * @property {string} imageUrl       - Pre-sized URL for this color's Kissen image. For colors in the server map it is the Kissen-only variant's own featured_media (merchant-set). For past-cap colors resolved client-side it starts '' and is filled in by #resolveImage from the variant-scoped section render's JSON-LD. May stay '' if that lookup fails.
 */

/**
 * Kissen-collection card corrector.
 *
 * On the `collection.kissenbezuege` template, `snippets/product-card.liquid` renders
 * each `<product-card>` with `data-kissen-context="true"` and an embedded
 * `<script type="application/json" data-kissen-variant-map>` holding one
 * {@link KissenVariantEntry} per color that has a standalone Kissen variant.
 *
 * The card's own `assets/product-card.js` already propagates a swatch's
 * `data-first-available-or-first-variant-id` onto the card links via its
 * public `applyVariantToLinks`. But a swatch change also triggers a
 * `section-rendering-product-card` re-fetch scoped only to the chosen Farbe —
 * a request that has NO way to know it is in the Kissen context (Liquid can't
 * read arbitrary query params). So after that re-render settles, the card's
 * links and price can briefly reflect Shopify's first variant for the color
 * (possibly a Kissen+Decke combo) rather than the Kissen-only one.
 *
 * This element re-asserts the Kissen-only variant from the embedded map after
 * every `productSelect`, and swaps the static Kissen `<img>` to match.
 *
 * Colours whose Kissen-only variant sits past Shopify's 250-variant Liquid cap
 * are rendered by the server as swatches marked `data-kissen-unresolved`. This
 * element resolves each speculatively on first hover/focus/touch/change via two
 * `priority: 'low'` Section Rendering fetches — `#resolveColor` (Farbe-scoped:
 * the bare Kissen-only variant id + link data) then `#resolveImage`
 * (variant-scoped: the Kissen image URL from the render's JSON-LD) — after which
 * it is treated like any mapped colour.
 *
 * @see docs/superpowers/specs/2026-08-29-kissen-collection-design.md
 * @see docs/superpowers/specs/2026-09-08-kissen-collection-250-cap-fix-design.md
 * @see docs/superpowers/specs/2026-09-08-kissen-collection-past-cap-image-design.md
 */
class KissenCollectionCard extends HTMLElement {
  /** @type {Map<string, KissenVariantEntry>} keyed by farbeValueId */
  #byValueId = new Map();

  /** @type {Map<string, KissenVariantEntry>} keyed by lowercased farbe text */
  #byFarbe = new Map();

  /**
   * farbeValueIds of swatches the server rendered with data-kissen-unresolved
   * (their Kissen-only variant sits past Shopify's 250-variant Liquid cap, or
   * the colour has none). Populated on connect, drained by #resolveColor.
   * @type {Set<string>}
   */
  #unresolved = new Set();

  /**
   * farbeValueIds a #resolveColor fetch is in flight for OR has finished (found
   * or not) — so hover + change on the same swatch don't double-fetch.
   * @type {Set<string>}
   */
  #resolveAttempted = new Set();

  /**
   * farbeValueIds a #resolveImage (layer-2, `&variant=` → JSON-LD) fetch has
   * been kicked off for. Separate from #resolveAttempted so the image lookup
   * dedupes independently of the variant-id lookup.
   * @type {Set<string>}
   */
  #imageAttempted = new Set();

  /**
   * Aborts every in-flight #resolveColor fetch on disconnect. NOT used to
   * supersede one resolve with another — each colour is an independent request
   * (at most ~5 per card, deduped by #resolveAttempted), so there's nothing to
   * cancel mid-life; aborting a colour's own fetch would just strand it.
   */
  #resolveAbortController = new AbortController();

  /** The product path (no query) for Section Rendering fetches, e.g. "/products/breezee". */
  #productPath = '';

  /** The section id to fetch for Farbe-scoped variant data (from the card's data attr). */
  #configuratorSectionId = 'product-information';

  connectedCallback() {
    const card = this.#card;
    if (!card) return;

    // A facet filter / sort on the collection grid morph-reorders this card,
    // disconnecting then reconnecting the element. #resolveAbortController is a
    // field initializer (created once) that disconnectedCallback aborts, so
    // without this re-arm every #resolveColor fetch after the first reorder
    // aborts instantly and the feature silently dies. Also clear
    // #resolveAttempted so a swatch morph re-rendered back to
    // data-kissen-unresolved can be resolved again.
    if (this.#resolveAbortController.signal.aborted) {
      this.#resolveAbortController = new AbortController();
      this.#resolveAttempted.clear();
      this.#imageAttempted.clear();
    }

    this.#parseMap();
    card.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#configuratorSectionId =
      this.dataset.configuratorSectionId || this.#configuratorSectionId;
    this.#productPath = this.#deriveProductPath();

    /** @type {NodeListOf<HTMLInputElement>} */
    const unresolvedInputs = card.querySelectorAll(
      'swatches-variant-picker-component input[data-kissen-unresolved="true"]'
    );

    // Non-Bettwäsche product mistakenly in the collection: the server map is
    // empty AND every swatch is unresolved. Resolving would fire one fetch per
    // colour only to remove every swatch. Leave the card as-is.
    const totalSwatches = card.querySelectorAll(
      'swatches-variant-picker-component input[type="radio"]'
    ).length;
    if (this.#byValueId.size === 0 && unresolvedInputs.length === totalSwatches && totalSwatches > 0) {
      return;
    }

    for (const input of unresolvedInputs) {
      const valueId = input.dataset.optionValueId;
      if (valueId) this.#unresolved.add(valueId);
      // pointerenter (desktop hover) + focusin (keyboard) + touchstart (mobile
      // tap, defensive fallback where pointerenter doesn't fire for touch) all
      // speculatively resolve — matching configurator-variant-picker.js's
      // #preloadSwatchGallery triggers. change is the guaranteed backstop on
      // actual select. #resolveAttempted / #imageAttempted dedupe multi-fire.
      input.addEventListener('pointerenter', this.#handleUnresolvedInteraction, { passive: true });
      input.addEventListener('focusin', this.#handleUnresolvedInteraction);
      input.addEventListener('touchstart', this.#handleUnresolvedInteraction, { passive: true });
      input.addEventListener('change', this.#handleUnresolvedInteraction);
    }
  }

  disconnectedCallback() {
    const card = this.#card;
    card?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    for (const input of card?.querySelectorAll(
      'swatches-variant-picker-component input'
    ) ?? []) {
      input.removeEventListener('pointerenter', this.#handleUnresolvedInteraction);
      input.removeEventListener('focusin', this.#handleUnresolvedInteraction);
      input.removeEventListener('touchstart', this.#handleUnresolvedInteraction);
      input.removeEventListener('change', this.#handleUnresolvedInteraction);
    }
    this.#resolveAbortController.abort();
  }

  /** The `<product-card>` this element wraps content inside of. */
  get #card() {
    return this.closest('product-card');
  }

  /**
   * The product path with no query string, from the card's overlay link
   * (`a.product-card__link`), e.g. "/products/breezee". This is the only
   * reliable source when the server map is empty (all colours past the cap).
   * @returns {string}
   */
  #deriveProductPath() {
    const link = this.#card?.querySelector('a.product-card__link');
    if (!(link instanceof HTMLAnchorElement) || !link.getAttribute('href')) return '';
    try {
      const url = new URL(link.href);
      return url.pathname;
    } catch {
      return '';
    }
  }

  /**
   * pointerenter / focusin / touchstart / change on a still-unresolved swatch —
   * speculatively kick off (or, if already attempted, skip) its resolution:
   * #resolveColor (variant id + link data) then #resolveImage (card image).
   * Safe to run on a mere hover — it fills caches and the input's own data-*,
   * never any "selected" state.
   * @param {Event} event
   */
  #handleUnresolvedInteraction = (event) => {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;
    this.#resolveColor(input);
  };

  /**
   * Resolve one past-cap colour's Kissen-only variant via a Farbe-scoped
   * Section Rendering fetch of the configurator block's embedded variant JSON
   * (cap-safe: one colour = ~15 variants, not the whole 315-variant catalogue).
   *
   * On success: add a {@link KissenVariantEntry} to the maps and fill the
   * input's data-variant-id / data-first-available-or-first-variant-id /
   * data-has-available-variant; drop data-kissen-unresolved; and if this swatch
   * is the checked one, push the id onto the card links immediately.
   *
   * On "no Kissen-only variant for this colour": remove the swatch's <li>
   * (overflow-list re-counts itself via its childList MutationObserver).
   *
   * On fetch failure: leave the swatch unresolved; a later interaction retries.
   *
   * @param {HTMLInputElement} input
   */
  async #resolveColor(input) {
    const valueId = input.dataset.optionValueId;
    if (!valueId || this.#resolveAttempted.has(valueId)) return;
    if (!this.#productPath || !this.#configuratorSectionId) return;
    this.#resolveAttempted.add(valueId);

    const url = `${this.#productPath}?section_id=${encodeURIComponent(
      this.#configuratorSectionId
    )}&option_values=${encodeURIComponent(valueId)}`;

    /** @type {Array<{id:number,farbe:string,kissenGroesse:string,deckeGroesse:string,available:boolean}>} */
    let variants;
    try {
      const response = await fetch(url, {
        priority: 'low',
        signal: this.#resolveAbortController.signal,
      });
      if (!response.ok) {
        console.warn('[kissen-collection] resolve fetch HTTP', response.status, 'for', input.value);
        this.#resolveAttempted.delete(valueId); // allow retry on transient 5xx / config error
        return;
      }
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = html.querySelector('configurator-variant-picker script[type="application/json"]');
      if (!script?.textContent) {
        // Not a configurator PDP / section absent — can't resolve. Leave it, but
        // don't retry endlessly: #resolveAttempted already blocks a re-run.
        console.warn('[kissen-collection] no configurator variant data for', input.value);
        return;
      }
      variants = JSON.parse(script.textContent);
      if (!Array.isArray(variants)) {
        console.warn('[kissen-collection] configurator variant data not an array for', input.value);
        // Malformed section render — treat as transient (a facet-filter reconnect
        // also clears the set), consistent with the !response.ok / catch branches.
        this.#resolveAttempted.delete(valueId);
        return;
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[kissen-collection] resolve fetch failed for', input.value, error);
        // Allow a retry on the next interaction.
        this.#resolveAttempted.delete(valueId);
      }
      return;
    }

    const kissenOnly = variants.find(
      (v) => v && v.kissenGroesse && (v.deckeGroesse === '' || v.deckeGroesse == null)
    );

    if (!kissenOnly) {
      // Colour genuinely has no Kissen-only variant — remove the swatch.
      this.#unresolved.delete(valueId);
      input.closest('li')?.remove();
      return;
    }

    // Same colour's Decke-paired sibling, if any — already in this same Layer-1
    // response, no extra fetch needed to find IT. Used below to decide whether
    // kissenOnly has a genuinely dedicated image or is still sharing the combo shot.
    const deckeSibling = variants.find((v) => v && v.deckeGroesse && v.deckeGroesse !== '');

    // --- Dedicated-image check ---
    // Mirrors the server-side rule in variant-swatches.liquid /
    // card-gallery.liquid / product-card.liquid: a bare-Kissen variant with no
    // image, or one whose image still matches its colour's Decke-paired
    // sibling's, is not Kissen-ready — remove the swatch exactly like the
    // !kissenOnly branch above. No sibling at all → dedicated by definition
    // (nothing to share the image WITH), matching the server rule. See
    // docs/superpowers/specs/2026-09-23-kissen-collection-dedicated-image-filter-design.md §3-4.5.
    //
    // Fetches kissenOnly's own image here (rather than leaving it to the
    // #resolveImage call below) only when a sibling exists to compare
    // against — #resolveImage's own no-sibling path is untouched and still
    // fetches lazily, unchanged from today.
    /** @type {string | null} '' = confirmed no image; null = not yet fetched or fetch failed; a URL = success. */
    let kissenOnlyImageUrl = '';
    if (deckeSibling) {
      let siblingImageUrl;
      try {
        [kissenOnlyImageUrl, siblingImageUrl] = await Promise.all([
          this.#fetchVariantImage(kissenOnly.id),
          this.#fetchVariantImage(deckeSibling.id),
        ]);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[kissen-collection] dedicated-image check failed for', input.value, error);
          this.#resolveAttempted.delete(valueId); // allow retry
        }
        return;
      }

      if (kissenOnlyImageUrl === '') {
        // Fetch succeeded and confirmed no image at all on the bare-Kissen
        // variant — genuinely not dedicated.
        this.#unresolved.delete(valueId);
        input.closest('li')?.remove();
        return;
      }
      if (kissenOnlyImageUrl && siblingImageUrl && siblingImageUrl === kissenOnlyImageUrl) {
        // Both fetches succeeded and the images match — still sharing the
        // combo shot, not dedicated.
        this.#unresolved.delete(valueId);
        input.closest('li')?.remove();
        return;
      }
      // kissenOnlyImageUrl === null (the fetch itself failed) or
      // siblingImageUrl is null/'' (fetch failed / no JSON-LD match) is
      // treated as "can't prove not-dedicated" — fail open, same posture as
      // every other best-effort branch in this file. #resolveImage's own
      // later lazy fetch (below) gets another chance at kissenOnly's image
      // when kissenOnlyImageUrl is null here (it seeds entry.imageUrl from
      // it, but `null` is falsy, so #resolveImage's own guard won't treat it
      // as already-resolved — see the entry construction below).
    }

    /** @type {KissenVariantEntry} */
    const entry = {
      farbe: input.value,
      farbeValueId: valueId,
      variantId: kissenOnly.id,
      variantUrl: `${this.#productPath}?variant=${kissenOnly.id}`,
      // Already fetched above when a Decke sibling existed AND the fetch
      // succeeded — seed it so the #resolveImage call below is a guaranteed
      // no-op instead of re-fetching the same variant. Stays '' when there
      // was no sibling to check against (unchanged, zero-fetch path). A
      // failed kissenOnly fetch during the dedication check (`null`) is
      // deliberately NOT seeded here (KissenVariantEntry.imageUrl is typed
      // string, and '' is this type's own "not yet resolved" sentinel
      // elsewhere in the file) — leaving it '' means #resolveImage's guard
      // (`entry.imageUrl || ...`) doesn't treat it as already-resolved, so
      // #resolveImage gets a real, fresh retry at fetching this variant's
      // image instead of the dedication check's failed attempt being final.
      imageUrl: kissenOnlyImageUrl || '',
    };
    if (kissenOnlyImageUrl) this.#imageAttempted.add(valueId);
    this.#byValueId.set(String(valueId), entry);
    if (typeof entry.farbe === 'string') this.#byFarbe.set(entry.farbe.toLowerCase(), entry);
    this.#unresolved.delete(valueId);

    input.removeAttribute('data-kissen-unresolved');
    input.dataset.variantId = String(kissenOnly.id);
    input.dataset.firstAvailableOrFirstVariantId = String(kissenOnly.id);
    input.dataset.hasAvailableVariant = kissenOnly.available ? 'true' : 'false';

    // A click that beat the fetch: make the card links correct now.
    const card = this.#card;
    if (input.checked && card && typeof card.applyVariantToLinks === 'function') {
      card.applyVariantToLinks(String(kissenOnly.id));
    }

    // Layer 2: fetch this colour's Kissen image from its variant-scoped section
    // render's JSON-LD (a no-op if the dedicated-image check above already got
    // it). Fire-and-forget — the link already works; the image is a
    // best-effort enhancement. `.catch` so a throw in the post-fetch parse
    // can't surface as an unhandled rejection.
    this.#resolveImage(entry).catch((error) => {
      console.warn('[kissen-collection] #resolveImage threw for', entry.farbe, error);
    });
  }

  /**
   * Fetches one variant's section render (`?section_id=...&variant=<id>`) and
   * extracts its JSON-LD image via `#extractVariantImage`. Shared by
   * `#resolveImage` (populating a resolved colour's card image) and
   * `#resolveColor`'s dedicated-image check (comparing a bare-Kissen variant's
   * image against its colour's Decke-paired sibling's). `priority: 'low'`,
   * aborted alongside every other resolve fetch via `#resolveAbortController`.
   *
   * Distinguishes two different kinds of "no image" so callers can fail open
   * on the one that isn't proof of anything: `null` means the FETCH ITSELF
   * failed (non-OK response, network error) — nothing was learned about this
   * variant's image, and treating that as "confirmed no image" would be wrong
   * (see `#resolveColor`'s dedicated-image check, which must not delete a
   * swatch over a transient network hiccup). `''` means the fetch succeeded
   * but the JSON-LD genuinely had no `#variant` image match — a real,
   * confirmed absence. `#resolveImage`'s own use doesn't need this
   * distinction (either result is just "nothing to show yet, retry later"),
   * so it treats both as falsy.
   *
   * @param {number} variantId
   * @returns {Promise<string | null>} the image URL, '' if confirmed absent, or null if the fetch itself failed
   */
  async #fetchVariantImage(variantId) {
    const url = `${this.#productPath}?section_id=${encodeURIComponent(
      this.#configuratorSectionId
    )}&variant=${encodeURIComponent(variantId)}`;

    try {
      const response = await fetch(url, {
        priority: 'low',
        signal: this.#resolveAbortController.signal,
      });
      if (!response.ok) {
        console.warn('[kissen-collection] image fetch HTTP', response.status, 'for variant', variantId);
        return null;
      }
      return KissenCollectionCard.#extractVariantImage(await response.text(), variantId);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[kissen-collection] image fetch failed for variant', variantId, error);
        return null;
      }
      throw error; // let the caller's own AbortError handling (e.g. #resolveColor's) run
    }
  }

  /**
   * Layer 2 of past-cap resolution: fetch the variant-scoped section render and
   * set `entry.imageUrl` from its JSON-LD (`#fetchVariantImage`). If the
   * colour whose entry this is happens to be the one currently checked, swap
   * the visible card image now.
   *
   * Best-effort and independent of `#resolveColor`'s own success — a failure
   * here leaves the card image on the default colour; the link is unaffected.
   * `#imageAttempted` dedupes hover + focus + touch + change firing for the
   * same colour. A no-op when `entry.imageUrl` is already set — `#resolveColor`
   * seeds it directly when its dedicated-image check already fetched this same
   * variant's image, so this call becomes a guaranteed no-op in that case
   * rather than re-fetching the same variant a second time.
   *
   * @param {KissenVariantEntry} entry
   */
  async #resolveImage(entry) {
    if (entry.imageUrl || this.#imageAttempted.has(entry.farbeValueId)) return;
    if (!this.#productPath || !this.#configuratorSectionId) return;
    this.#imageAttempted.add(entry.farbeValueId);

    let imageUrl;
    try {
      imageUrl = await this.#fetchVariantImage(entry.variantId);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      throw error;
    }

    if (!imageUrl) {
      this.#imageAttempted.delete(entry.farbeValueId); // allow retry
      console.warn('[kissen-collection] no #variant image in JSON-LD for', entry.farbe);
      return;
    }

    entry.imageUrl = imageUrl;

    // If this colour's swatch is the one currently selected, update the image now.
    const checked = this.#checkedSwatchInput();
    if (checked && checked.dataset.optionValueId === entry.farbeValueId) {
      this.#swapImage(entry);
    }
  }

  #parseMap() {
    const script = this.querySelector('script[type="application/json"][data-kissen-variant-map]');
    if (!script?.textContent) {
      console.warn('[kissen-collection] no variant map found');
      return;
    }

    /** @type {KissenVariantEntry[]} */
    let entries = [];
    try {
      entries = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[kissen-collection] could not parse variant map', error);
      return;
    }

    for (const entry of entries) {
      if (entry?.farbeValueId != null) this.#byValueId.set(String(entry.farbeValueId), entry);
      if (typeof entry?.farbe === 'string') this.#byFarbe.set(entry.farbe.toLowerCase(), entry);
    }
  }

  /** @returns {HTMLInputElement | null} */
  #checkedSwatchInput() {
    const el = this.#card?.querySelector('swatches-variant-picker-component input[type="radio"]:checked');
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @param {string | undefined} valueId
   * @param {string | undefined} farbeText
   * @returns {KissenVariantEntry | undefined}
   */
  #lookup(valueId, farbeText) {
    if (valueId && this.#byValueId.has(String(valueId))) return this.#byValueId.get(String(valueId));
    if (farbeText && this.#byFarbe.has(farbeText.toLowerCase())) return this.#byFarbe.get(farbeText.toLowerCase());
    return undefined;
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    const card = this.#card;
    if (!card) return;

    const optionValueId = event.detail?.optionValueId;
    const checked = this.#checkedSwatchInput();
    const entry = this.#lookup(
      typeof optionValueId === 'string' ? optionValueId : checked?.dataset.optionValueId,
      checked?.value
    );
    if (!entry) return;

    // Swap the static image immediately (no need to wait for the section re-render).
    this.#swapImage(entry);

    // After product-card.js has morphed the card from the section response,
    // re-assert the Kissen-only variant on every card link. Relies on
    // product-card.js having registered its own event.promise.then first
    // (it binds the productSelect listener in its own connectedCallback,
    // which runs before this nested element's), so this callback runs after
    // that morph and wins. product-card.js's #updateLinks mutates the anchor
    // hrefs in place rather than replacing the nodes, so last-writer-wins holds.
    event.promise
      .then(() => {
        if (typeof card.applyVariantToLinks === 'function') {
          card.applyVariantToLinks(String(entry.variantId));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[kissen-collection] productSelect promise rejected', error);
        }
      });
  };

  /**
   * Swaps the static card image to this colour's Kissen image. Called only on
   * an actual colour change — the server already renders the default colour's
   * <img> (with its responsive srcset) on first paint, so there is no
   * initial-sync call. Once we swap to a single fixed-width source the old
   * srcset no longer matches, so it is dropped; a no-op guard skips the case
   * where productSelect fires for the colour that is already showing.
   * @param {KissenVariantEntry} entry
   */
  #swapImage(entry) {
    if (!entry.imageUrl) return;
    const img = this.#card?.querySelector('img[data-kissen-card-image]');
    if (!(img instanceof HTMLImageElement)) return;
    if (img.currentSrc === entry.imageUrl || img.src === entry.imageUrl) return;
    img.src = entry.imageUrl;
    img.removeAttribute('srcset');
  }

  /**
   * Pull one variant's image URL out of a product section render's JSON-LD.
   *
   * `?section_id=product-information&variant=<id>` embeds a
   * `<script type="application/ld+json">` whose Product block carries, per
   * variant, a fragment like:
   *   "…?variant=<id>#variant","@type":"Product","image":"<cdn url>",…
   * Regex, not JSON.parse: the ld+json is one large block among several
   * `<script>`s and we only need one field for one id. Shopify escapes
   * forward slashes (`\/`) and `&` (`&`) in the JSON string, so decode the
   * captured token as a JSON string rather than a single targeted replace.
   * Relies on `"image"` appearing before `"offers":{…}` in each hasVariant
   * entry — a `}` before `"image"` would make the match fail to `''` (graceful).
   *
   * @param {string} html - raw response text of the variant-scoped section render
   * @param {number} variantId
   * @returns {string} the image URL, or '' if not found
   */
  static #extractVariantImage(html, variantId) {
    const re = new RegExp(`variant=${variantId}#variant"[^}]*?"image":"([^"]+)"`);
    const match = html.match(re);
    if (!match) return '';
    // The captured token is a JSON string body — decode all escapes (\/, &, …),
    // not just the forward slash. `[^"]+` already stopped at the unescaped closing
    // quote, so wrapping in quotes and JSON.parse-ing it is safe; fall back to a
    // slash-only unescape if the store ever emits a token JSON.parse can't handle.
    try {
      return JSON.parse(`"${match[1]}"`);
    } catch {
      return match[1].replace(/\\\//g, '/');
    }
  }
}

if (!customElements.get('kissen-collection-card')) {
  customElements.define('kissen-collection-card', KissenCollectionCard);
}
